// Pure shaping only: the route holds the Supabase reads, so the unit suite can import this file.
import { isoDay, latestPerDay, parseNumeric } from '@/lib/app-detail/daily-buckets';

/**
 * `app_st.st_raw.price` is what this app charged in one storefront on one fetch.
 * `app_metadata.price` cannot do this job: it is an array, null on 198 of the newest 200 ChatGPT
 * us rows and null on every paid row sampled, which is why `timeline.ts`'s `price` field has never
 * drawn an event. `->>` selection answers text, so a row that does not parse is dropped.
 */
export type PriceRow = { created_at: string; price: string | number | null; iap?: string | boolean | null };

/** One run of fetches at the same price. The chart draws segments; the list is the boundaries between them. */
export type PriceSegment = { from: string; to: string; price: number };
export type PriceChange = { date: string; before: number; after: number; percent: number | null };

/** `currencyAssumed`: the storefront's currency is a guess, not a reading, and the card says so. */
export type PriceHistoryResult = {
  segments: PriceSegment[];
  changes: PriceChange[];
  meta: { currency: string; currencyAssumed: boolean; iap: boolean | null; fetches: number; firstAt: string | null; latestAt: string | null; free: boolean };
};

/** A change older than this is history, not news, so the header badge stays off. */
export const BADGE_WINDOW_DAYS = 90;

function toBoolean(value: string | boolean | null | undefined): boolean | null {
  if (value === null || value === undefined || value === '') return null;
  return typeof value === 'boolean' ? value : value === 'true';
}

/**
 * An exact price, never compacted. `formatCurrency` in `lib/utils` is a COMPACT formatter for
 * revenue totals and renders 9.99 as "$10"; a price card that rounds the price away has nothing
 * left to say.
 *
 * The fraction digits are ICU's, not ours: a hard-coded 2 printed "¥1,500.00" for the ¥1,500 app
 * 6743685377, and JPY, KRW, VND and CLP are all live in `app_metadata` today. Standard notation
 * agrees between Node and the browser; only the compact notation next door needs a floor.
 * An unusable code falls back to the bare amount rather than throwing a RangeError through render.
 */
export function formatPrice(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  } catch {
    return `${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  }
}

/**
 * Every price this app has charged in one storefront, as segments and the changes between them.
 * `free` is a claim about the ROWS GIVEN, not about the app: on a narrowed range it means every
 * fetch inside that range was free, and the card says which of the two it is showing.
 */
export function toPriceHistory(rows: readonly PriceRow[], currency: { code: string | null; assumed?: boolean }): PriceHistoryResult {
  const parsed = rows.filter((r) => parseNumeric(r.price) !== null);
  const daily = latestPerDay(parsed, (r) => isoDay(r.created_at), (r) => r.created_at);

  const segments: PriceSegment[] = [];
  for (const row of daily) {
    const price = parseNumeric(row.price) as number;
    const held = segments[segments.length - 1];
    if (held && held.price === price) held.to = isoDay(row.created_at);
    else segments.push({ from: isoDay(row.created_at), to: isoDay(row.created_at), price });
  }

  const changes: PriceChange[] = segments.slice(1).map((segment, i) => {
    const before = segments[i].price;
    // A rise from free has no percentage: dividing by zero is Infinity, not a story.
    const percent = before === 0 ? null : Math.round(((segment.price - before) / before) * 1000) / 10;
    return { date: segment.from, before, after: segment.price, percent };
  });

  const ordered = [...parsed].sort((a, b) => a.created_at.localeCompare(b.created_at));
  // The newest fetch that says anything about in-app purchases at all.
  const iap = ordered.reduce<boolean | null>((held, row) => toBoolean(row.iap) ?? held, null);

  return {
    segments,
    changes,
    meta: {
      currency: currency.code || 'USD',
      currencyAssumed: currency.assumed ?? !currency.code,
      iap,
      fetches: parsed.length,
      firstAt: ordered[0]?.created_at ?? null,
      latestAt: ordered[ordered.length - 1]?.created_at ?? null,
      free: segments.length > 0 && segments.every((s) => s.price === 0),
    },
  };
}

/** What the card renders. `ever` is the `all` range the header pill already holds (a cache hit): an error never hides, free over `all` is no card, 0 rows is "no data". */
export function priceCardState({ rangedError, everError, everLoading, everFree }: { rangedError: boolean; everError: boolean; everLoading: boolean; everFree: boolean }): 'error' | 'loading' | 'hidden' | 'card' {
  if (rangedError || everError) return 'error';
  if (everLoading) return 'loading';
  if (everFree) return 'hidden';
  return 'card';
}

/** The newest change inside the badge window, or null. The header pill is this and nothing else. */
export function recentPriceChange(result: PriceHistoryResult, now: Date = new Date()): PriceChange | null {
  const newest = result.changes[result.changes.length - 1];
  if (!newest) return null;
  const age = (now.getTime() - Date.parse(`${newest.date}T00:00:00Z`)) / 86_400_000;
  return age >= 0 && age <= BADGE_WINDOW_DAYS ? newest : null;
}
